'use client';

import { c as _c } from "react/compiler-runtime";
import { Combobox } from '@systemeio/components';
import { ChevronDownIcon } from '@systemeio/icons';
import { Fragment, useMemo } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useFormControlSize } from '../form-control-size-context';
import { cancelBackspaceClear, getSelectedOption, OptionsSkeleton, type FormSelectDataType, type FormSelectIdType, type FormSelectProps } from './form-select';
interface FormSelectDataWithGroupType<T extends FormSelectIdType = FormSelectIdType, G extends string = string> extends FormSelectDataType<T> {
  group: G;
}
interface FormSelectWithGroupsProps<T extends FormSelectIdType> extends Omit<FormSelectProps<T>, 'data' | 'statusIcon'> {
  data: FormSelectDataWithGroupType<T>[] | undefined;
  groups: {
    caption: string;
    id: string;
  }[];
}
interface FormSelectGroup<T extends FormSelectIdType> {
  caption: string;
  id: string;
  items: FormSelectDataWithGroupType<T>[];
}
function FormSelectWithGroups(t0) {
  const $ = _c(64);
  const {
    ariaLabel,
    data,
    dataTest,
    description,
    disabled,
    error,
    groups,
    inputRef,
    isPreFetching,
    label,
    onChange,
    onClose,
    onOpen,
    placeholder,
    required,
    size,
    staticData,
    value,
    withoutCloseIcon
  } = t0;
  const inheritedSize = useFormControlSize();
  const {
    t
  } = useLocoTranslation();
  const t1 = data ?? staticData;
  let t2;
  if ($[0] !== t1 || $[1] !== value) {
    t2 = getSelectedOption(value, t1);
    $[0] = t1;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selectedOption = t2;
  const isDisabled = disabled || isPreFetching || value !== undefined && !data && !staticData;
  let t3;
  if ($[3] !== placeholder || $[4] !== t) {
    t3 = placeholder ?? t("global.select");
    $[3] = placeholder;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const resolvedPlaceholder = t3;
  const accessibleLabel = ariaLabel ?? (typeof label === "string" ? label : resolvedPlaceholder);
  let t4;
  if ($[6] !== data || $[7] !== groups) {
    let t5;
    if ($[9] !== data) {
      t5 = group => ({
        ...group,
        items: (data ?? []).filter(option => option.group === group.id)
      });
      $[9] = data;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = groups.map(t5);
    $[6] = data;
    $[7] = groups;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const groupedData = t4;
  const t5 = isPreFetching || undefined;
  const t6 = label ?? resolvedPlaceholder;
  let t7;
  if ($[11] !== onClose || $[12] !== onOpen) {
    t7 = open => {
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    };
    $[11] = onClose;
    $[12] = onOpen;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== onChange || $[15] !== withoutCloseIcon) {
    t8 = (option_3, eventDetails) => {
      if (cancelBackspaceClear(withoutCloseIcon, eventDetails)) {
        return;
      }
      onChange(option_3?.id);
    };
    $[14] = onChange;
    $[15] = withoutCloseIcon;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = size ?? inheritedSize;
  const t10 = selectedOption as FormSelectDataWithGroupType<T> | null;
  let t11;
  if ($[17] !== label || $[18] !== required) {
    t11 = label !== undefined ? <Combobox.InputLabel required={required}>{label}</Combobox.InputLabel> : null;
    $[17] = label;
    $[18] = required;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = label === undefined ? accessibleLabel : undefined;
  const t13 = isPreFetching ? "animate-pulse" : undefined;
  const t14 = error ? "" : undefined;
  let t15;
  if ($[20] !== dataTest || $[21] !== inputRef) {
    t15 = <Combobox.Input {...dataTest} ref={inputRef} />;
    $[20] = dataTest;
    $[21] = inputRef;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== isDisabled || $[24] !== selectedOption || $[25] !== t || $[26] !== withoutCloseIcon) {
    t16 = !withoutCloseIcon && selectedOption && !isDisabled ? <Combobox.Clear aria-label={t("global.clear")} /> : null;
    $[23] = isDisabled;
    $[24] = selectedOption;
    $[25] = t;
    $[26] = withoutCloseIcon;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChevronDownIcon />;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== accessibleLabel) {
    t18 = <Combobox.Trigger aria-label={accessibleLabel}>{t17}</Combobox.Trigger>;
    $[29] = accessibleLabel;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t15 || $[35] !== t16 || $[36] !== t18) {
    t19 = <Combobox.InputGroup aria-label={t12} className={t13} data-invalid={t14}>{t15}{t16}{t18}</Combobox.InputGroup>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== data || $[39] !== t) {
    t20 = <Combobox.Content>{data === undefined ? <OptionsSkeleton /> : <><Combobox.Empty description={t("dashboard.droplist.default_description")}>{t("dashboard.droplist.default_title")}</Combobox.Empty><Combobox.List>{_temp5}</Combobox.List></>}</Combobox.Content>;
    $[38] = data;
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== accessibleLabel || $[42] !== groupedData || $[43] !== isDisabled || $[44] !== required || $[45] !== resolvedPlaceholder || $[46] !== t10 || $[47] !== t11 || $[48] !== t19 || $[49] !== t20 || $[50] !== t6 || $[51] !== t7 || $[52] !== t8 || $[53] !== t9) {
    t21 = <Combobox.Root aria-label={accessibleLabel} disabled={isDisabled} drawerTitle={t6} isItemEqualToValue={_temp} itemToStringLabel={_temp2} itemToStringValue={_temp3} items={groupedData} onOpenChange={t7} onValueChange={t8} placeholder={resolvedPlaceholder} required={required} size={t9} value={t10}>{t11}{t19}{t20}</Combobox.Root>;
    $[41] = accessibleLabel;
    $[42] = groupedData;
    $[43] = isDisabled;
    $[44] = required;
    $[45] = resolvedPlaceholder;
    $[46] = t10;
    $[47] = t11;
    $[48] = t19;
    $[49] = t20;
    $[50] = t6;
    $[51] = t7;
    $[52] = t8;
    $[53] = t9;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== description) {
    t22 = description ? <Combobox.Description>{description}</Combobox.Description> : null;
    $[55] = description;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== error) {
    t23 = error ? <Combobox.Error>{error}</Combobox.Error> : null;
    $[57] = error;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t21 || $[60] !== t22 || $[61] !== t23 || $[62] !== t5) {
    t24 = <div aria-busy={t5} className="grid w-full content-start gap-2">{t21}{t22}{t23}</div>;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t5;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
}
function _temp5(group_0, groupIndex) {
  return <Fragment key={group_0.id}>{groupIndex > 0 ? <Combobox.Separator /> : null}<Combobox.Group items={group_0.items}><Combobox.GroupLabel>{group_0.caption}</Combobox.GroupLabel><Combobox.GroupOptions>{_temp4}</Combobox.GroupOptions></Combobox.Group></Fragment>;
}
function _temp4(option_4) {
  return <Combobox.Item data-test-id={option_4.dataTestAttribute} disabled={option_4.disabled} key={String(option_4.id)} value={option_4}><Combobox.ItemText>{option_4.customCaption ?? option_4.caption}</Combobox.ItemText>{option_4.additionalRender}<Combobox.ItemIndicator /></Combobox.Item>;
}
function _temp3(option_2) {
  return String(option_2.id);
}
function _temp2(option_1) {
  return option_1.caption;
}
function _temp(option_0, selected) {
  return Object.is(option_0.id, selected.id);
}
export { FormSelectWithGroups };
export type { FormSelectDataWithGroupType, FormSelectWithGroupsProps };